import { FC, JSX } from 'react';
import cn from 'classnames';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';

export enum HighlightType {
  Inactive,
  Loading,
  Normal,
  Warning,
}

export enum DetailsViewType {
  Amount,
  Date,
  Enum,
  Footer,
  FooterWithLink,
  Header,
  Interest,
  InterestType,
  Link,
  Months,
  PeriodMonths,
  Text,
}

export interface Note {
  invisible?: boolean;
  prefix?: string;
  suffix?: string;
  type?: DetailsViewType;
  value: string;
  warning?: boolean;
}

export interface ViewItem {
  label?: string;
  negative?: boolean;
  notopmargin?: boolean;
  prefix?: string;
  suffix?: string;
  suffixNotTranslated?: boolean;
  type: DetailsViewType;
  value: string;
  invisible?: boolean;
  notes?: Note[];
  key?: string;
}

interface DetailsProps {
  loadingMessage?: string;
  loading?: boolean;
  view: ViewItem[];
}

export const NOT_AVAILABLE = 'n/a';

export const Details: FC<DetailsProps> = (props) => {
  const showDetail = (viewItem: ViewItem | Note) => {
    const prefix = viewItem.prefix ? viewItem.prefix : '';
    const suffix = viewItem.suffix ? viewItem.suffix : '';
    return `${prefix} ${viewItem.value} ${suffix}`.trim();
  };

  const notes = (keyOffset: number, notes: Note[] | undefined): JSX.Element[] => {
    if (notes === undefined) return [];
    return notes
      .filter((note) => note.invisible !== true)
      .map((note, i) => {
        if (note.type === DetailsViewType.FooterWithLink) {
          return (
            <div
              key={-keyOffset * 100 + i}
              className="text-[0.9rem] mt-0 mb-0 mr-[10%] break-words flex flex-col"
            >
              <span className="whitespace-nowrap">{note.prefix}</span>&nbsp;
              <a
                className="text-blue-600 underline"
                href={note.value}
                target="_blank"
                rel="noopener noreferrer"
              >
                {note.value}
              </a>
              &nbsp;{note.suffix}
            </div>
          );
        }
        return (
          <div
            key={-keyOffset * 100 + i}
            className={cn(
              "flex justify-between mt-0 mb-0",
              note.warning ? "text-[#0057b8]" : "",
              "text-[0.9rem]"
            )}
          >
            <div>{showDetail(note)}</div>
          </div>
        );
      });
  };

  const isVisible = (viewItem: ViewItem): boolean => {
    if (viewItem.invisible === true) return false;
    return true;
  };

  if (props.loading === true) return <LoadingSpinner />;

  const block: JSX.Element[] = [];

  props.view.filter(isVisible).forEach((detailViewElement, index) => {
    switch (detailViewElement.type) {
      case DetailsViewType.Amount:
      case DetailsViewType.Enum:
      case DetailsViewType.Interest:
      case DetailsViewType.InterestType:
      case DetailsViewType.Months:
      case DetailsViewType.PeriodMonths:
      case DetailsViewType.Text:
        block.push(
          <div key={index} className="flex justify-between mt-4">
            <div>{detailViewElement.label}</div>
            <div className="font-bold text-black whitespace-nowrap data-hj-suppress">{showDetail(detailViewElement)}</div>
          </div>
        );
        break;
      case DetailsViewType.Date:
        block.push(
          <div key={index} className="flex justify-between mt-4">
            <div className="whitespace-nowrap">{detailViewElement.label}</div>
            <div className="font-bold text-black whitespace-nowrap data-hj-suppress">{showDetail(detailViewElement)}</div>
          </div>
        );
        break;
      case DetailsViewType.Link:
        block.push(
          <div key={index} className="flex justify-between mt-4">
            <div>{detailViewElement.label}</div>
            <div className="font-bold text-black whitespace-nowrap data-hj-suppress">
              <a className="text-blue-600 underline" href={'http://' + detailViewElement.value} target="_blank" rel="noopener noreferrer">
                {detailViewElement.value}
              </a>
            </div>
          </div>
        );
        break;
      case DetailsViewType.Header:
        block.push(
          <div key={index} className="flex justify-between mt-0 font-bold text-black">
            <div>{detailViewElement.value}</div>
          </div>
        );
        break;
      case DetailsViewType.FooterWithLink:
        block.push(
          <div
            key={index}
            className="text-[0.9rem] mt-0 mb-0 mr-[10%] break-words flex flex-col"
          >
            <span className="whitespace-nowrap">{detailViewElement.prefix}</span>&nbsp;
            <a className="text-blue-600 underline" href={detailViewElement.value} target="_blank" rel="noopener noreferrer">
              {detailViewElement.value}
            </a>
            &nbsp;{detailViewElement.suffix}
          </div>
        );
        break;
    }
    block.push(...notes(index, detailViewElement.notes));
  });

  return <>{block}</>;
};
